import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Trash2, Upload } from 'lucide-react';
import type { BatchSummary } from '@tekvoice/shared';
import { useAuth } from '@/auth/auth-context';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { StatusCountsRow } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/components/ui/sonner';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

/** SPEC §11 — the home screen: every (non-deleted) batch with its per-status counts. */
export function BatchesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [search, setSearch] = React.useState('');
  const [pendingDelete, setPendingDelete] = React.useState<BatchSummary | null>(null);

  const batches = useQuery({
    queryKey: ['batches', search],
    // Soft-deleted batches stay hidden (SPEC §7).
    queryFn: () => api.batches.list({ ...(search ? { q: search } : {}) }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.batches.remove(id),
    onSuccess: async (result) => {
      toast.success(`Deleted “${result.batch.name}”`);
      setPendingDelete(null);
      await queryClient.invalidateQueries({ queryKey: ['batches'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const rows = batches.data?.batches ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Batches</h1>
          <p className="text-sm text-muted-foreground">
            Import a bilingual CSV, generate audio, review and approve.
          </p>
        </div>
        {can('batch.import') ? (
          <Button onClick={() => navigate('/import')}>
            <Upload className="h-4 w-4" />
            Import CSV
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
          <div>
            <CardTitle>All batches</CardTitle>
            <CardDescription>
              {batches.data ? `${batches.data.total} batch(es)` : 'Loading…'}
            </CardDescription>
          </div>
          <Input
            className="w-64"
            placeholder="Search by name…"
            value={search}
            aria-label="Search batches"
            onChange={(event) => setSearch(event.target.value)}
          />
        </CardHeader>
        <CardContent>
          {batches.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <FileSpreadsheet className="h-8 w-8 text-muted-foreground" aria-hidden />
              <div>
                <p className="font-medium">
                  {search ? 'No batches match that search' : 'No batches yet'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {search
                    ? 'Try a different name.'
                    : 'Import a CSV with english_text and arabic_text columns to get started.'}
                </p>
              </div>
              {!search && can('batch.import') ? (
                <Button variant="outline" onClick={() => navigate('/import')}>
                  <Upload className="h-4 w-4" />
                  Import CSV
                </Button>
              ) : null}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch</TableHead>
                  <TableHead className="w-20 text-right">Rows</TableHead>
                  <TableHead className="w-28 text-right">Approved</TableHead>
                  <TableHead>Audio status</TableHead>
                  <TableHead>Uploaded by</TableHead>
                  <TableHead>Created</TableHead>
                  {can('batch.delete') ? <TableHead className="w-16" /> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>
                      <Link
                        to={`/batches/${batch.id}`}
                        className="font-medium underline-offset-4 hover:underline"
                      >
                        {batch.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">{batch.sourceFilename}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{batch.rowCount}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {batch.approvedRowCount} / {batch.rowCount}
                    </TableCell>
                    <TableCell>
                      <StatusCountsRow counts={batch.assetCounts} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {batch.uploadedByName ?? '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatDateTime(batch.createdAt)}
                    </TableCell>
                    {can('batch.delete') ? (
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Delete ${batch.name}`}
                          onClick={() => setPendingDelete(batch)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title={`Delete “${pendingDelete?.name ?? ''}”?`}
        description="The batch is hidden from this list. Nothing is removed from Google Cloud Storage and the audit trail is kept."
        confirmLabel="Delete batch"
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />
    </div>
  );
}
